/**
 * @name DataGrid
 * @description Componente común de grid configurable para acciones CRUD
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Material-UI
import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableRow from '@material-ui/core/TableRow';

import { EmployeeDetail, LoadingWrapper, NoRegistersFound } from '@components';
import { FiltersService } from '@services';
import { MUI } from '@styles';

import Actions from './components/actions';
import Header from './components/header';
import Pagination from './components/pagination';
import RowsPerPage from './components/rows-per-page';

class DataGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      order: 'asc',
      orderBy: '',
      currentPage: 0,
      pageSize: 10,
      selectedRows: [],
      newSelectedResp: null
    };
  }

  resetState = () => {
    this.setState({
      order: 'asc',
      orderBy: '',
      selectedRows: [],
    });
  }

  // *************************************
  // SORT
  // *************************************

  /**
   * Maneja las peticiones de ordenamiento y actualiza el estado
   * @param {object} event
   * @param {string} property { column.id }
   */
  requestSortHandler = (event, property) => {
    const { order, orderBy } = this.state;
    const isAsc = orderBy === property && order === 'asc';
    this.setState({
      order: isAsc ? 'desc' : 'asc',
      orderBy: property
    });
  }

  sort = (array, comparator) => {
    const stabilizedThis = array.map((element, index) => [element, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((element) => element[0]);
  }

  getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => this.descendingComparator(a, b, orderBy)
      : (a, b) => -this.descendingComparator(a, b, orderBy);
  }

  descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // *************************************
  // SELECT
  // *************************************

  /**
   * Selecciona todos o deselecciona todos los row's
   * @param {any} row
   */
  selectAllHandler = (event) => {
    const rows = this.props.rows;

    if (event.target.checked) {
      const newSelecteds = rows;
      this.setState({ selectedRows: newSelecteds });
      return;
    }
    this.setState({ selectedRows: [] });
  };

  /**
   * Selecciona o deselecciona un row 
   * @param {any} row
   */
  selectRowHandler = (event, row) => {
    const { selectedRows } = this.state;
    const { canSelectMultiple } = this.props;

    const selectedIndex = selectedRows.indexOf(row);

    let newSelected = [];

    if (selectedRows.length > 0 && canSelectMultiple) {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRows, row);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRows.slice(1));
      } else if (selectedIndex === selectedRows.length - 1) {
        newSelected = newSelected.concat(selectedRows.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRows.slice(0, selectedIndex),
          selectedRows.slice(selectedIndex + 1),
        );
      }
    } else {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(row);
        this.state.newSelectedResp = newSelected;
        this.props.onClickRow && this.props.onClickRow(newSelected);
      } else {
        this.props.onClickRow && this.props.onClickRow([]);
      }
    }
    this.setState({ selectedRows: newSelected });
  }

  /**
   * Regresa el row seleccionado
   * @param {any} rowSelected
   * @return {any} row
   */
  clickRowHandler = (rowSelected) => {
    return rowSelected;
  }

  /**
   * Valida que un row esté seleccionado
   * @param {any} row
   * @return {boolean}
   */
  isRowSelected = (row) => {
    const { selectedRows } = this.state;
    return selectedRows.indexOf(row) !== -1;
  }

  // *************************************
  // PAGINATION
  // *************************************

  changePageSizeHandler = (event) => {
    this.setState({ pageSize: parseInt(event.target.value, 10) });
    this.setState({ currentPage: 0 });
  };

  changePageHandler = (event, page) => {
    this.setState({ currentPage: page - 1 });
  }

  render() {
    const {
      columns = [],
      rows = [],
      canAdd,
      canEdit,
      canDelete,
      canSelect,
      canSelectAll,
      canSort,
      isPaginated,
      onAdd,
      onEdit,
      onDelete,
    } = this.props;

    let {
      order,
      orderBy,
      currentPage,
      pageSize,
      selectedRows,
      newSelectedResp
    } = this.state;

    pageSize = isPaginated ? pageSize : rows.length;

    return (
      <>
        <LoadingWrapper area="loading-datagrid">
          <Styled.DataGridWrapper>
            {(isPaginated || canAdd || canEdit || canDelete) && (
              <Styled.FlexToolbar>
                {isPaginated && (
                  <>
                    <RowsPerPage
                      rowsLength={rows.length}
                      pageSize={pageSize}
                      currentPage={currentPage}
                      onChangePageSize={this.changePageSizeHandler}
                    />
                    {Math.ceil(rows.length / pageSize) > 1 && (
                      <Pagination
                        rowsLength={rows.length}
                        pageSize={pageSize}
                        currentPage={currentPage}
                        onChangePage={this.changePageHandler}
                      />
                    )}
                  </>
                )}
                <Actions
                  canAdd={canAdd}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  selectedRows={selectedRows}
                  selectedRowsLength={selectedRows.length}
                  onAdd={onAdd}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              </Styled.FlexToolbar>
            )}

            <Styled.TableContainer>
              <Styled.Table>
                <Header
                  columns={columns}
                  rowsLength={rows.length}
                  selectedRowsLength={selectedRows.length}
                  canSelect={canSelect}
                  canSelectAll={canSelectAll}
                  canSort={rows.length !== 0 ? canSort : false}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={this.requestSortHandler}
                  onSelectAll={this.selectAllHandler}
                />

                <Styled.TableBody>
                  {this.sort(rows, this.getComparator(order, orderBy))
                    .slice(currentPage * pageSize, currentPage * pageSize + pageSize)
                    .map((row, index) => {
                      const isItemSelected = canSelect ? this.isRowSelected(row) : false;
                      const labelId = `table-checkbox-${index}`;

                      return (
                        <Styled.TableRow
                          key={labelId}
                          role="checkbox"
                          hover={canSelect}
                          aria-checked={isItemSelected}
                          selected={isItemSelected}
                          tabIndex={-1}
                          onClick={canSelect ? (event) => this.selectRowHandler(event, row) : null}
                        >
                          {/* Checkbox */}
                          {canSelect && (
                            <Styled.TableCell padding="checkbox">
                              <MUI.Checkbox
                                checked={isItemSelected}
                                inputProps={{ 'aria-labelledby': labelId }}
                              />
                            </Styled.TableCell>
                          )}
                          {/* Registros */}
                          {columns.map((column) => (
                            column.visible && (
                              column.type === 'complex' ? (
                                <Styled.TableCell key="complex" align={column.align}>
                                  <EmployeeDetail
                                    avatar={row[column.id[0]]}
                                    avatarSize="xs"
                                    employeeName={row[column.id[1]]}
                                    employeeDetail={row[column.id[2]]}
                                  />
                                </Styled.TableCell>
                              ) : (
                                <Styled.TableCell key={column.id} align={column.align}>
                                  { newSelectedResp === null ?
                                    column.filter ? FiltersService[column.filter](row[column.id], false) : row[column.id] :
                                    newSelectedResp[0].Id === row.Id ?
                                      column.filter ? FiltersService[column.filter](row[column.id], true) : row[column.id] :
                                      column.filter ? FiltersService[column.filter](row[column.id], false) : row[column.id]}
                                </Styled.TableCell>
                              )
                            )
                          ))}
                        </Styled.TableRow>
                      );
                    })}
                </Styled.TableBody>
              </Styled.Table>

              {/* No Registers Found */}
              {rows.length === 0 && (
                <NoRegistersFound />
              )}
            </Styled.TableContainer>
          </Styled.DataGridWrapper>
        </LoadingWrapper>
      </>
    );
  }
}

DataGrid.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  // Modelo: [{ id: string, label: string, visible: boolean, filter: string/null, minWidth: int/null, sort: boolean, align: left/right }
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array
      ]).isRequired,
      label: PropTypes.string.isRequired,
      visible: PropTypes.bool.isRequired,
      filter: PropTypes.string,
      minWidth: PropTypes.number,
      sort: PropTypes.bool,
      align: PropTypes.oneOf(['left', 'right'])
    })
  ),
  // Registros mostrados actualmente en el grid.
  rows: PropTypes.array.isRequired,
  // Boolean que indica si debe ocultar/mostrar el botón de añadir.
  canAdd: PropTypes.bool,
  // Boolean que indica si debe ocultar/mostrar el botón de editar.
  canEdit: PropTypes.bool,
  // Boolean que indica si debe ocultar/mostrar el botón de eliminar.
  canDelete: PropTypes.bool,
  // Boolean que indica si se muestran los checkbox para seleccionar elementos.
  canSelect: PropTypes.bool,
  // Boolean que indica si se muestra el checkbox para seleccionar todos los elementos.
  canSelectAll: PropTypes.bool,
  // Boolean que indica si se puede tener una multiple selección de elementos.
  canSelectMultiple: PropTypes.bool,
  // Boolean que indica si se puede ordenar las columnas.
  canSort: PropTypes.bool,
  // Boolean que indica si los registros están paginados.
  isPaginated: PropTypes.bool,
  // Función a ejecutar cuando se agregará un elemento nuevo, al invocarla es necesario pasar como argumento event.
  onAdd: PropTypes.func,
  // Función a ejecutar cuando se editará un elemento, al invocarla es necesario pasar como argumento event.
  onEdit: PropTypes.func,
  // Función a ejecutar cuando se desea eliminar elementos, al invocarla es necesario pasar como argumento event.
  onDelete: PropTypes.func,
  // Función a ejecutar cuando se selecciona un elemento.
  onClickRow: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.DataGridWrapper = styled.div`
  width: 100%;
`;

Styled.FlexToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 8px 0;
`;

// TableContainer
Styled.TableContainer = styled(MuiTableContainer)`
  width: 100%;
  overflow: auto;
`;

// Table
Styled.Table = styled(MuiTable)``;

// TableBody
Styled.TableBody = styled(MuiTableBody)``;

// TableRow
Styled.TableRow = styled(MuiTableRow)`
  &.MuiTableRow-root {
    transition: .5s all;
  }

  &.MuiTableRow-root.Mui-selected {
    background-color: ${(props) => props.theme.colors.greyHover};
  }

  &.MuiTableRow-root.MuiTableRow-hover:hover {
    background-color: ${(props) => props.theme.colors.greyHover};
    cursor: pointer;
  }
`;

// TableCell
Styled.TableCell = styled(MuiTableCell)`
  &.MuiTableCell-root {
    padding: 12px;
    font-family: 'Bryant Pro Regular';
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: 1.4;
    letter-spacing: normal;
    vertical-align: middle;
    color: ${(props) => props.theme.colors.greyDark};
    border-bottom: 1px solid ${(props) => props.theme.colors.greyHover};
  }

  &.MuiTableCell-paddingCheckbox {
    padding: 4px;
  }
`;

export default DataGrid;